import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link2 } from 'lucide-react';
import { ajouterUtm } from '../../lib/utm';

// ─── Types ───────────────────────────────────────────────────────────────────
type CalcTab = 'plusvalue' | 'dividende' | 'frais';
const VALID_TABS: CalcTab[] = ['plusvalue', 'dividende', 'frais'];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (n: number) => Math.round(n).toLocaleString('fr-FR');
const pct = (n: number) => n.toFixed(2) + ' %';

// ─── Calculateur 1 : Plus-value ───────────────────────────────────────────────
const PlusvalueCalc: React.FC = () => {
  const [achat, setAchat] = useState('');
  const [vente, setVente] = useState('');
  const [qte, setQte] = useState('');
  const [dividendes, setDividendes] = useState('');
  const [annees, setAnnees] = useState('');

  const pa = parseFloat(achat) || 0;
  const pv = parseFloat(vente) || 0;
  const q = parseFloat(qte) || 0;
  const div = parseFloat(dividendes) || 0;
  const n = parseFloat(annees) || 0;

  const investi = pa * q;
  const valeurVente = pv * q;
  const gainCapital = valeurVente - investi;
  const gainTotal = gainCapital + div;
  const rendPct = investi > 0 ? (gainTotal / investi) * 100 : 0;
  const rendAnnualise = investi > 0 && n > 0
    ? (Math.pow((valeurVente + div) / investi, 1 / n) - 1) * 100
    : null;

  const hasResult = pa > 0 && pv > 0 && q > 0;
  const isPositif = gainTotal >= 0;

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={labelStyle}>
            <span>Prix d'achat (FCFA)</span>
            <input style={inputStyle} type="number" placeholder="ex: 28000" value={achat} onChange={e => setAchat(e.target.value)} />
          </label>
          <label style={labelStyle}>
            <span>Prix de vente (FCFA)</span>
            <input style={inputStyle} type="number" placeholder="ex: 32000" value={vente} onChange={e => setVente(e.target.value)} />
          </label>
        </div>
        <label style={labelStyle}>
          <span>Nombre d'actions</span>
          <input style={inputStyle} type="number" placeholder="ex: 100" value={qte} onChange={e => setQte(e.target.value)} />
        </label>
        <label style={labelStyle}>
          <span>Dividendes reçus au total (FCFA, optionnel)</span>
          <input style={inputStyle} type="number" placeholder="ex: 150000" value={dividendes} onChange={e => setDividendes(e.target.value)} />
        </label>
        <label style={labelStyle}>
          <span>Durée de détention (années, optionnel)</span>
          <input style={inputStyle} type="number" placeholder="ex: 3" value={annees} onChange={e => setAnnees(e.target.value)} />
        </label>
      </div>

      <div style={{ flex: '1 1 220px', background: 'var(--lp-bg-2)', borderRadius: 14, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {hasResult ? (
          <>
            <ResultRow label="Capital investi" value={`${fmt(investi)} FCFA`} />
            <ResultRow label="Valeur à la vente" value={`${fmt(valeurVente)} FCFA`} />
            <ResultRow label="Gain en capital" value={`${gainCapital >= 0 ? '+' : ''}${fmt(gainCapital)} FCFA`} color={gainCapital >= 0 ? '#16a34a' : '#ef4444'} />
            {div > 0 && <ResultRow label="Dividendes perçus" value={`+${fmt(div)} FCFA`} color="#16a34a" />}
            <div style={{ borderTop: '1px solid var(--lp-border)', paddingTop: 12 }}>
              <ResultRow label="Gain total" value={`${isPositif ? '+' : ''}${fmt(gainTotal)} FCFA`} color={isPositif ? '#16a34a' : '#ef4444'} large />
              <ResultRow label="Rendement total" value={`${isPositif ? '+' : ''}${pct(rendPct)}`} color={isPositif ? '#16a34a' : '#ef4444'} />
              {rendAnnualise !== null && (
                <ResultRow label="Rendement annualisé" value={`${rendAnnualise >= 0 ? '+' : ''}${pct(rendAnnualise)}/an`} color={rendAnnualise >= 0 ? '#16a34a' : '#ef4444'} />
              )}
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--lp-text-muted)', fontSize: 13, paddingTop: 16 }}>
            Renseignez les champs pour<br />voir votre plus-value estimée
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Calculateur 2 : Rendement Dividende ─────────────────────────────────────
const DividendeCalc: React.FC = () => {
  const [cours, setCours] = useState('');
  const [div, setDiv] = useState('');

  const p = parseFloat(cours) || 0;
  const d = parseFloat(div) || 0;

  const yield_ = p > 0 ? (d / p) * 100 : 0;
  const hasResult = p > 0 && d > 0;

  // Référentiels indicatifs pour contextualiser (taux approximatifs, non contractuels)
  const refs = [
    { label: 'Livret épargne (fourchette BCEAO)', rate: 3.5, color: '#94a3b8' },
    { label: "Obligations d'État UEMOA (indicatif)", rate: 6.0, color: '#64748b' },
    { label: 'Actions BRVM (rendement hist. moy.)', rate: 4.5, color: '#8b5cf6' },
  ];

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={labelStyle}>
          <span>Cours actuel de l'action (FCFA)</span>
          <input style={inputStyle} type="number" placeholder="ex: 28500" value={cours} onChange={e => setCours(e.target.value)} />
        </label>
        <label style={labelStyle}>
          <span>Dividende annuel par action (FCFA)</span>
          <input style={inputStyle} type="number" placeholder="ex: 1400" value={div} onChange={e => setDiv(e.target.value)} />
        </label>
        {hasResult && (
          <div style={{ marginTop: 8, background: 'var(--lp-bg-2)', borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ fontSize: 13, color: 'var(--lp-text-muted)', marginBottom: 10 }}>
              Comparaison avec d'autres placements
            </div>
            {refs.map(r => {
              const barW = Math.min((r.rate / Math.max(yield_, r.rate, 8)) * 100, 100);
              return (
                <div key={r.label} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--lp-text-muted)', marginBottom: 3 }}>
                    <span>{r.label}</span><span style={{ fontWeight: 700, color: r.color }}>{pct(r.rate)}</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(0,0,0,0.08)', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${barW}%`, background: r.color, borderRadius: 3 }} />
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                <span style={{ fontWeight: 700 }}>Votre action</span>
                <span style={{ fontWeight: 800, color: yield_ >= 5 ? '#16a34a' : yield_ >= 3 ? '#f59e0b' : '#ef4444' }}>{pct(yield_)}</span>
              </div>
              <div style={{ height: 6, background: 'rgba(0,0,0,0.08)', borderRadius: 3 }}>
                <div style={{ height: '100%', width: `${Math.min((yield_ / Math.max(yield_, 8)) * 100, 100)}%`, background: yield_ >= 5 ? '#16a34a' : yield_ >= 3 ? '#f59e0b' : '#ef4444', borderRadius: 3 }} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: '1 1 220px', background: 'var(--lp-bg-2)', borderRadius: 14, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {hasResult ? (
          <>
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 42, fontWeight: 900, color: yield_ >= 5 ? '#16a34a' : yield_ >= 3 ? '#f59e0b' : '#ef4444', lineHeight: 1 }}>
                {pct(yield_)}
              </div>
              <div style={{ fontSize: 13, color: 'var(--lp-text-muted)', marginTop: 6 }}>
                de rendement dividende
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--lp-border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ResultRow label="Pour 100 000 FCFA investis" value={`${fmt((d / p) * 100000)} FCFA/an`} />
              <ResultRow label="Payback estimé" value={`${(p / d).toFixed(1)} ans`} />
              <div style={{ marginTop: 4, padding: '10px 12px', borderRadius: 8, background: 'rgba(100,116,139,0.07)', fontSize: 12, color: 'var(--lp-text-muted)', fontStyle: 'italic' }}>
                {yield_ >= 6 ? '📊 Rendement supérieur à 6 % sur la base du dividende saisi.' : yield_ >= 4 ? '📊 Rendement entre 4 % et 6 % sur la base du dividende saisi.' : '📊 Rendement inférieur à 4 % sur la base du dividende saisi.'}
              </div>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--lp-text-muted)', fontSize: 13, paddingTop: 16 }}>
            Renseignez les champs pour<br />calculer le rendement
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Calculateur 3 : Frais BRVM ──────────────────────────────────────────────
// Source : BNI Finances & Attijariwafa (marché BRVM, taux standards)
const FRAIS_BRVM = {
  courtage_sgi: 0.01,      // 1.0% courtage SGI (taux standard marché)
  tps_sur_courtage: 0.10,  // 10% TPS (taxe État) sur le montant du courtage → 0.1% du brut
  frais_marche: 0.003,     // 0.3% frais de marché (BRVM + DCBR, incompressibles)
  // Total par opération : 1.0% + 0.1% + 0.3% = 1.4%
};

const FraisCalc: React.FC = () => {
  const [montant, setMontant] = useState('');
  const [operation, setOperation] = useState<'aller' | 'aller_retour'>('aller_retour');

  const m = parseFloat(montant) || 0;

  const calcFrais = (montantOp: number) => {
    const courtage = montantOp * FRAIS_BRVM.courtage_sgi;
    const tps = courtage * FRAIS_BRVM.tps_sur_courtage;
    const marche = montantOp * FRAIS_BRVM.frais_marche;
    const total = courtage + tps + marche;
    return { courtage, tps, marche, total };
  };

  const fraisAchat = calcFrais(m);
  const fraisVente = calcFrais(m);
  const totalAR = fraisAchat.total + fraisVente.total;
  const hasResult = m > 0;

  const totalDisplay = operation === 'aller' ? fraisAchat.total : totalAR;
  const pctFrais = m > 0 ? (totalDisplay / m) * 100 : 0;

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={labelStyle}>
          <span>Montant de la transaction (FCFA)</span>
          <input style={inputStyle} type="number" placeholder="ex: 500000" value={montant} onChange={e => setMontant(e.target.value)} />
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          {([['aller', 'Achat seul'], ['aller_retour', 'Achat + Revente']] as const).map(([v, l]) => (
            <button
              key={v}
              onClick={() => setOperation(v)}
              style={{
                flex: 1, padding: '10px 8px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: operation === v ? '2px solid #6C3FC4' : '1px solid var(--lp-border)',
                background: operation === v ? 'rgba(108,63,196,0.08)' : 'transparent',
                color: operation === v ? '#6C3FC4' : 'var(--lp-text)',
              }}
            >{l}</button>
          ))}
        </div>
        {hasResult && (
          <div style={{ background: 'var(--lp-bg-2)', borderRadius: 12, padding: '14px 16px', fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 10, color: 'var(--lp-text)' }}>
              Détail {operation === 'aller' ? "de l'achat" : "aller-retour (×2)"}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--lp-text-muted)' }}>
                <span>Courtage SGI (1,0%{operation === 'aller_retour' ? ' ×2' : ''})</span>
                <span>{fmt(operation === 'aller' ? fraisAchat.courtage : fraisAchat.courtage * 2)} FCFA</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--lp-text-muted)' }}>
                <span>TPS sur courtage — État (0,1%{operation === 'aller_retour' ? ' ×2' : ''})</span>
                <span>{fmt(operation === 'aller' ? fraisAchat.tps : fraisAchat.tps * 2)} FCFA</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--lp-text-muted)' }}>
                <span>Frais marché BRVM/DCBR (0,3%{operation === 'aller_retour' ? ' ×2' : ''})</span>
                <span>{fmt(operation === 'aller' ? fraisAchat.marche : fraisAchat.marche * 2)} FCFA</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: '1 1 220px', background: 'var(--lp-bg-2)', borderRadius: 14, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {hasResult ? (
          <>
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: 38, fontWeight: 900, color: '#6C3FC4', lineHeight: 1 }}>
                {fmt(totalDisplay)} <span style={{ fontSize: 18 }}>FCFA</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--lp-text-muted)', marginTop: 6 }}>
                de frais {operation === 'aller' ? "à l'achat" : "aller-retour"}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#6C3FC4', marginTop: 4 }}>
                ({pct(pctFrais)} du montant)
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--lp-border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ResultRow label="Capital net investi" value={`${fmt(m - fraisAchat.total)} FCFA`} />
              {operation === 'aller_retour' && (
                <ResultRow label="Seuil de rentabilité" value={`+${pct(pctFrais)} minimum`} />
              )}
              <div style={{ marginTop: 4, padding: '12px 14px', borderRadius: 8, background: 'rgba(108,63,196,0.06)', fontSize: 12, color: 'var(--lp-text)', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 Frais de transaction uniquement</div>
                <p style={{ margin: '0 0 8px' }}>Ce calculateur estime uniquement les frais liés à l'exécution de votre ordre. Selon votre SGI, d'autres frais peuvent s'appliquer :</p>
                <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <li><strong>Frais de tenue de compte</strong> — forfait fixe (annuel ou trimestriel)</li>
                  <li><strong>Droits de garde</strong> — % prélevé sur la valeur de votre portefeuille</li>
                  <li><strong>Bourse en ligne</strong> — abonnement mensuel à la plateforme</li>
                  <li><strong>Frais administratifs</strong> — virements, retraits, transferts de titres</li>
                </ul>
                <p style={{ margin: '8px 0 0', color: 'var(--lp-text-muted)' }}>Consultez toujours les conditions tarifaires complètes de votre courtier.</p>
              </div>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--lp-text-muted)', fontSize: 13, paddingTop: 16 }}>
            Entrez un montant pour<br />voir le détail des frais
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Calculateur 4 : SAFCA DPS ───────────────────────────────────────────────
// Augmentation de capital SAFCA — Ratio 13 DPS → 6 actions nouvelles à 400 FCFA


// ─── Shared UI helpers ────────────────────────────────────────────────────────
const ResultRow: React.FC<{ label: string; value: string; color?: string; large?: boolean }> = ({ label, value, color, large }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
    <span style={{ fontSize: large ? 13 : 12, color: 'var(--lp-text-muted)' }}>{label}</span>
    <span style={{ fontSize: large ? 15 : 13, fontWeight: large ? 800 : 600, color: color || 'var(--lp-text)', whiteSpace: 'nowrap' }}>{value}</span>
  </div>
);

const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 5,
  fontSize: 12, fontWeight: 600, color: 'var(--lp-text-muted)',
};

const inputStyle: React.CSSProperties = {
  padding: '10px 12px', borderRadius: 8, fontSize: 14,
  border: '1px solid var(--lp-border)', background: 'var(--lp-bg-2)',
  color: 'var(--lp-text)', outline: 'none', width: '100%', boxSizing: 'border-box',
};

// ─── Tabs config ──────────────────────────────────────────────────────────────
const TABS: { id: CalcTab; emoji: string; label: string; sub: string; badge?: string }[] = [
  { id: 'plusvalue', emoji: '📈', label: 'Simulateur de gain', sub: 'Plus-value + dividendes' },
  { id: 'dividende', emoji: '💰', label: 'Rendement dividende', sub: 'Yield vs. autres placements' },
  { id: 'frais', emoji: '🧾', label: 'Frais de transaction', sub: 'Coût réel BRVM' },
];

// ─── Main Section ─────────────────────────────────────────────────────────────
const CalculatorsSection: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);

  const rawTab = searchParams.get('calc') as CalcTab | null;
  const activeTab: CalcTab = rawTab && VALID_TABS.includes(rawTab) ? rawTab : 'plusvalue';

  const setActiveTab = useCallback((tab: CalcTab) => {
    setSearchParams({ calc: tab }, { replace: true });
  }, [setSearchParams]);

  // Liens de partage marqués en UTM (S-D6) : sans cela, une visite venue d'un partage se
  // confond avec le trafic direct. Le lien copié part vers une destination inconnue
  // (WhatsApp le plus souvent) : medium « referral », source « lien_copie ».
  const lienPartage = (source: string, medium = 'social') =>
    ajouterUtm(window.location.href, { source, medium, campaign: 'partage_outils' });

  const copyLink = () => {
    navigator.clipboard.writeText(lienPartage('lien_copie', 'referral')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <section style={{ padding: '40px 20px 60px', background: 'var(--lp-bg)' }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 12, marginTop: 40 }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 20px', borderRadius: 12, cursor: 'pointer',
                border: activeTab === tab.id ? '2px solid #6C3FC4' : '1px solid var(--lp-border)',
                background: activeTab === tab.id ? 'rgba(108,63,196,0.07)' : 'var(--lp-bg-2)',
                color: activeTab === tab.id ? '#6C3FC4' : 'var(--lp-text)',
                transition: 'all 0.15s', position: 'relative',
              }}
            >
              {tab.badge && (
                <span style={{
                  position: 'absolute', top: -8, right: -6,
                  background: '#6C3FC4', color: '#fff',
                  fontSize: 9, fontWeight: 800, padding: '2px 6px',
                  borderRadius: 20, letterSpacing: 0.3,
                }}>{tab.badge}</span>
              )}
              <span style={{ fontSize: 20 }}>{tab.emoji}</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{tab.label}</div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>{tab.sub}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Boutons partage social */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <span style={{ fontSize: 11, color: 'var(--lp-text-muted)', marginRight: 4 }}>Partager :</span>

          {/* Facebook */}
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(lienPartage('facebook'))}`}
            target="_blank" rel="noopener noreferrer"
            title="Partager sur Facebook"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: 8, textDecoration: 'none',
              background: '#1877F2', color: '#fff', fontSize: 14, fontWeight: 900,
              flexShrink: 0,
            }}
          >f</a>

          {/* X / Twitter */}
          <a
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(lienPartage('x'))}&text=${encodeURIComponent('Calculateur BRVM — Kaneabourse')}`}
            target="_blank" rel="noopener noreferrer"
            title="Partager sur X (Twitter)"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: 8, textDecoration: 'none',
              background: '#000', color: '#fff', fontSize: 13, fontWeight: 900,
              flexShrink: 0,
            }}
          >𝕏</a>

          {/* LinkedIn */}
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(lienPartage('linkedin'))}`}
            target="_blank" rel="noopener noreferrer"
            title="Partager sur LinkedIn"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: 8, textDecoration: 'none',
              background: '#0A66C2', color: '#fff', fontSize: 13, fontWeight: 900,
              flexShrink: 0,
            }}
          >in</a>

          {/* Copier le lien */}
          <button
            onClick={copyLink}
            title="Copier le lien"
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
              border: '1px solid var(--lp-border)',
              background: copied ? 'rgba(22,163,74,0.08)' : 'var(--lp-bg-2)',
              color: copied ? '#16a34a' : 'var(--lp-text-muted)',
              fontSize: 12, fontWeight: 600, transition: 'all 0.2s',
            }}
          >
            <Link2 size={13} />
            {copied ? 'Copié !' : 'Lien'}
          </button>
        </div>

        {/* Calculator card */}
        <div style={{
          background: 'var(--lp-bg-2)', border: '1px solid var(--lp-border)',
          borderRadius: 20, padding: '28px 28px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          {activeTab === 'plusvalue' && <PlusvalueCalc />}
          {activeTab === 'dividende' && <DividendeCalc />}
          {activeTab === 'frais' && <FraisCalc />}
        </div>

        <div style={{ marginTop: 20, padding: '14px 18px', borderRadius: 12, background: 'rgba(100,116,139,0.06)', border: '1px solid rgba(100,116,139,0.14)', fontSize: 11, color: '#64748b', lineHeight: 1.6, textAlign: 'justify' }}>
          <strong>AVERTISSEMENT LÉGAL :</strong> Les informations, analyses et signaux fournis par <strong>KaneaBourse</strong> sont générés par des modèles algorithmiques et d'intelligence artificielle (Nia). Ils sont fournis par <strong>KANEA Technologies</strong> à titre purement informatif et ne constituent en aucun cas un conseil en investissement, une sollicitation ou une recommandation d'achat ou de vente. <strong>KANEA Technologies</strong> n'est pas une Société de Gestion et d'Intermédiation (SGI) ni un Conseiller en Investissement Financier (CIF) agréé par l'AMF-UEMOA. L'investissement en bourse comporte des risques de perte en capital. Les performances passées ne préjugent pas des performances futures.
        </div>
      </div>
    </section>
  );
};

export default CalculatorsSection;